import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Der Zustand einer Liste — in der URL, nicht im Speicher der Komponente.
 *
 * Was gesucht, gefiltert, sortiert und geblättert wird, steht in der Adresse:
 *
 *     /instruments?q=intel&exchange=NASDAQ&sort=isin-&page=2
 *
 * Das ist kein Schönheitsgewinn, sondern behebt vier Dinge auf einmal: Der
 * Zustand übersteht das Neuladen, die Zurück-Taste nimmt den letzten Schritt
 * zurück statt die Seite zu verlassen, ein Link auf eine gefilterte Liste
 * lässt sich weitergeben, und zwei Ansichten teilen sich keinen Zustand mehr.
 *
 * Werte, die dem Standard entsprechen, werden NICHT geschrieben. Sonst stünde
 * hinter jeder Liste `?sort=name&page=1`, und der Unterschied zwischen
 * "nichts eingestellt" und "genau so eingestellt" wäre nicht mehr zu sehen.
 *
 * DIE RICHTUNG HÄNGT AM SPALTENNAMEN, nicht an einem zweiten Parameter:
 *
 *     ?sort=title      aufsteigend
 *     ?sort=title-     absteigend
 *
 * Ein Minus und kein Plus für die Gegenrichtung: In Query-Strings steht `+`
 * für ein Leerzeichen. `?sort=title+` liest sich als `"title "` — wer die
 * Adresse von Hand tippt oder weitergibt, bekommt nicht, was er meint.
 * Kodiert ginge es (`%2B`), aber dann ist die Adresse nicht mehr lesbar, und
 * genau das war der Zweck.
 *
 * Das Zeichen sagt die Richtung ABSOLUT, nicht relativ zur Vorgabe: `title`
 * ist aufsteigend, auch auf einer Seite, die absteigend vorbelegt ist. Sonst
 * bedeutete derselbe Link auf zwei Seiten Verschiedenes.
 */
export interface ListParams<F extends Record<string, string>> {
  /** Die Filterwerte, wie sie in der URL stehen. */
  filters: F
  sort: string
  descending: boolean
  page: number
  /** Setzt einen Filter und springt auf Seite 1 zurück. */
  setFilter: (name: keyof F & string, value: string) => void
  /**
   * MEHRERE Filter auf einmal.
   *
   * Nicht dasselbe wie zwei `setFilter` hintereinander — die gehen verloren:
   * Beide bauen ihre neue Adresse aus demselben `params` dieses Renderns auf,
   * und der zweite überschreibt den ersten stillschweigend. Aufgefallen beim
   * Wechsel der Norm auf der Branchenseite: Der Reiter sprang zurück, weil das
   * Zurücksetzen der offenen Knoten den Normwechsel wieder wegnahm.
   */
  setFilters: (values: Partial<Record<keyof F & string, string>>) => void
  setSort: (column: string, descending: boolean) => void
  setPage: (page: number) => void
  /** Alles auf Standard — die Adresse wird wieder nackt. */
  reset: () => void
  /** Weicht irgendetwas vom Standard ab? Für den Zurücksetzen-Knopf. */
  touched: boolean
}

interface Defaults<F> {
  /** Die Filter mit ihren leeren Werten, etwa `{ q: '', exchange: '' }`. */
  filters: F
  /**
   * Vorbelegung aus DATEN statt aus einer Konstanten.
   *
   * `filters` trägt die statische Vorgabe — was in der Datei steht. `preset`
   * trägt eine, die erst zur Laufzeit feststeht: die Favoriten unter den
   * Handelsplätzen etwa, die aus der Datenbank kommen. Solange sie noch nicht
   * geladen sind, darf der Wert leer bleiben; der Hook nimmt dann die
   * statische Vorgabe.
   *
   * WICHTIG für die aufrufende Seite: Ihre Abfrage sollte warten, bis die
   * Grundlage da ist. Sonst läuft die erste ungefiltert und wird von der
   * gefilterten sofort abgelöst — sichtbares Umspringen und eine schwere
   * Abfrage umsonst. `useListQuery` hat dafür seinen `enabled`-Parameter.
   */
  preset?: Partial<Record<keyof F & string, string>>
  sort: string
  descending?: boolean
}

/**
 * Steht in der Adresse für "ausdrücklich nichts gewählt".
 *
 * Ohne dieses Zeichen wäre eine Vorbelegung nicht abzuschütteln: Ein Filter
 * auf den leeren Wert zu setzen LÖSCHT ihn aus der Adresse (siehe `apply`),
 * und damit griffe die Vorbelegung sofort wieder. Wer alle Handelsplätze
 * abwählt, bekäme die Favoriten zurück und käme aus dem Filter nicht heraus.
 *
 * Ein Minus, weil es kein gültiger Filterwert ist — kein MIC, keine Währung,
 * kein Status — und in einer Adresse nicht maskiert werden muss. Es wird nur
 * geschrieben, wo es auch ein `preset` gibt; ohne Vorbelegung bleibt alles
 * beim Alten.
 */
const LEER = '-'

export function useListParams<F extends Record<string, string>>(
  defaults: Defaults<F>,
): ListParams<F> {
  const [params, setParams] = useSearchParams()

  //: Was gilt, wenn die Adresse nichts sagt: die Vorbelegung, sonst die
  //: statische Vorgabe. An zwei Stellen gebraucht — beim Lesen und beim
  //: Vergleich für `touched` —, und beide müssen dieselbe Antwort geben.
  const standard = useCallback((name: keyof F & string) =>
    defaults.preset?.[name] || defaults.filters[name], [defaults])

  const filters = useMemo(() => {
    const current = {} as F
    for (const name of Object.keys(defaults.filters)) {
      const roh = params.get(name);
      // `as` ist hier nötig, weil TypeScript den Schlüssel eines generischen
      // Objekts nicht zum Indexsignatur-Zugriff zulässt.
      (current as Record<string, string>)[name] =
        roh === LEER ? '' : roh ?? standard(name as keyof F & string)
    }
    return current
    // `params.toString()` als Abhängigkeit: Das Objekt selbst wechselt bei
    // jedem Rendern die Identität, sein Inhalt nicht.
  }, [params.toString(), standard])   // eslint-disable-line react-hooks/exhaustive-deps

  // Spalte und Richtung aus EINEM Wert.
  //
  // `title-` heisst absteigend, `title` aufsteigend, nichts heisst Vorgabe.
  // Das Minus wird nur am ENDE gelesen: Ein Spaltenname darf selbst Bindestriche
  // tragen, und `last-material-update-` waere sonst nicht zu zerlegen.
  const roh = params.get('sort')
  const sort = roh ? roh.replace(/-$/, '') : defaults.sort
  const descending = roh
    // ALTE ADRESSEN WEITERLESEN. Bis eben trug die Richtung ein eigener
    // Parameter: `?sort=name&dir=desc`. Solche Adressen stehen in
    // Lesezeichen und im Verlauf, und ohne diesen Zweig zeigten sie
    // stillschweigend das Gegenteil — die Liste sähe richtig aus und wäre es
    // nicht.
    //
    // Das Suffix gewinnt, wenn beides dasteht: Es ist die neuere Angabe.
    ? (roh.endsWith('-') || params.get('dir') === 'desc')
    : (defaults.descending ?? false)
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1)

  /**
   * Schreibt die Änderung in die Adresse.
   *
   * `replace` beim Tippen: Ohne das hinterließe "NASDAQ" sechs Einträge im
   * Verlauf, und die Zurück-Taste müsste sechsmal gedrückt werden, um einen
   * Schritt zurückzukommen. Beim Sortieren und Blättern ist ein Eintrag
   * dagegen richtig — das ist ein Schritt, den man zurücknehmen will.
   */
  const apply = useCallback((changes: Record<string, string | number | null>,
                            replace: boolean) => {
    const next = new URLSearchParams(params)
    for (const [name, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(name)
      else next.set(name, String(value))
    }
    setParams(next, { replace })
  }, [params, setParams])

  const setFilter = useCallback((name: keyof F & string, value: string) => {
    // Leeren heisst bei einem vorbelegten Filter NICHT löschen: Der Parameter
    // müsste sonst aus der Adresse verschwinden, und die Vorbelegung wäre
    // sofort zurück. Nur dort, wo es eine gibt — sonst bleibt es beim Alten.
    const geleert = value === '' && Boolean(defaults.preset?.[name])
    // Filter geändert heißt: wieder auf Seite 1. Sonst stünde man auf Seite 4
    // einer Trefferliste, die nur noch zwei Seiten hat.
    apply({ [name]: geleert ? LEER : value || null, page: null }, true)
  }, [apply, defaults])

  const setFilters = useCallback(
    (values: Partial<Record<keyof F & string, string>>) => {
      // EIN `apply` für alle: Zwei Aufrufe nacheinander lesen dasselbe
      // `params` und der zweite verwirft die Änderung des ersten.
      const changes: Record<string, string | null> = {}
      for (const [name, value] of Object.entries(values)) {
        const geleert = value === '' && Boolean(defaults.preset?.[name])
        changes[name] = geleert ? LEER : (value || null)
      }
      apply({ ...changes, page: null }, true)
    }, [apply, defaults])

  const setSort = useCallback((column: string, desc: boolean) => {
    // Nur wegzulassen, wenn Spalte UND Richtung der Vorgabe entsprechen —
    // nicht, wenn die Richtung zufällig "aufsteigend" lautet.
    //
    // Hier stand einmal `dir: desc ? 'desc' : null`. Das nahm an, dass eine
    // fehlende Angabe immer aufsteigend bedeutet. Auf sechs Seiten ist die
    // Vorgabe aber ABSTEIGEND — Kurse, Historie, Meldungen, Handelsplätze,
    // Handelsideen und Research zeigen das Jüngste zuerst. Dort schrieb ein
    // Klick auf "aufsteigend" gar nichts in die Adresse, beim Lesen griff
    // wieder die Vorgabe, und die Richtung liess sich überhaupt nicht
    // umkehren: Der zweite Klick auf dieselbe Spalte tat sichtbar nichts.
    const vorgabe = defaults.descending ?? false
    const wieVorgabe = column === defaults.sort && desc === vorgabe
    apply({
      sort: wieVorgabe ? null : (desc ? `${column}-` : column),
      // Der alte Parameter wird geräumt, falls er noch in einer gespeicherten
      // Adresse steht — sonst stünde er stumm daneben und niemand wüsste, ob
      // er noch gilt.
      dir: null,
      page: null,
    }, false)
  }, [apply, defaults.sort, defaults.descending])

  const setPage = useCallback((next: number) => {
    apply({ page: next <= 1 ? null : next }, false)
  }, [apply])

  const reset = useCallback(() => { setParams({}, { replace: false }) },
                            [setParams])

  // Gegen den WIRKSAMEN Standard, nicht gegen die statische Vorgabe: Sonst
  // wäre der Zurücksetzen-Knopf schon beim Öffnen aktiv, obwohl niemand etwas
  // berührt hat — die Vorbelegung ist ja der Ausgangszustand.
  const touched = Object.keys(defaults.filters).some(
    (name) => filters[name] !== standard(name as keyof F & string))
    || sort !== defaults.sort
    || descending !== (defaults.descending ?? false)
    || page !== 1

  return { filters, sort, descending, page,
           setFilter, setFilters, setSort, setPage, reset, touched }
}
